
import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { getUserByToken, login } from '../core/_requests'
import { collectLoginInfo } from '../core/AuthHelpers'
import { toAbsoluteUrl } from '../../../../_myb/helpers'
import { useAuth } from '../core/Auth'
import { loginApi, socialLogin } from '../../../api'
import { AuthModel, UserModel } from '../core/_models'
import { signInWithGooglePopup } from '../core/Firebase'
import { LoginDto, SocialLogins } from '../../../types'
import { brandSettings } from '../../settings/BrandSettings'
import { toast } from 'react-toastify'
import AppleSignInButton from './AppleLogin'

// Add helper function at the top (after imports, before the component)
const LAST_BIZ_KEY = 'lastSelectedBiz';

const getStoredBizPreference = (userEmail: string): { bizId?: string; branchId?: string } | null => {
  try {
    const stored = localStorage.getItem(`${LAST_BIZ_KEY}_${userEmail}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading stored biz preference:', error);
    return null;
  }
};

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
})

const initialValues = {
  email: localStorage.getItem('userEmail') || 'demo@mybooksai.app', // Get from localStorage or use demo email
  password: (localStorage.getItem('userEmail')) ? '' : 'Welcome@123',
};

export interface LoginResponse {
  token: string;
  user: UserModel;
}

export const doLogin = async (loginDto: LoginDto): Promise<LoginResponse | undefined> => {
  const auth: AuthModel = {
    api_token: '',
    refreshToken: undefined
  }
  // const {saveAuth, setCurrentUser} = useAuth()
  try {
    if (loginDto.email) {
      const storedPref = getStoredBizPreference(loginDto.email);
      if (storedPref?.bizId) {
        loginDto.bizId = storedPref.bizId;
      }
    }
    loginDto.loginInfo = collectLoginInfo();
    const { token } = await loginApi(loginDto)

    if (token) {
      auth.api_token = token
      // saveAuth(auth)

      const user = await getUserByToken(token)
      // Assuming the user object contains an email field
      if (user && user.email) {
        // Store email in localStorage
        localStorage.setItem('userEmail', user.email);
      }
      // setCurrentUser(user)
      return { token, user }
    } else {
      throw new Error("Login Failed!")
    }

  } catch (error) {
    console.error(error)
    return undefined
  }
}

// Shared public demo account (also used by the /demo landing page)
export const DEMO_CREDENTIALS = { email: 'demo@mybooksai.app', password: 'Welcome@123' };

export const authModel = {
  api_token: '',
  refreshToken: undefined,
};

export function Login() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { saveAuth, saveCurrentUser } = useAuth();
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Update the handleGoogleSignIn function to include loading state:
  const handleGoogleSignIn = async (response: any) => {
    setGoogleLoading(true);
    try {
      const token = response.credential; // Get the Google ID token from the response

      // Send the token to your backend for authentication
      const result = await socialLogin({
        sToken: token,
        provider: SocialLogins.Google,
        onlyLogin: false,
        loginInfo: collectLoginInfo(),
      });

      if (result) {
        authModel.api_token = result.token;
        saveAuth(authModel);
        const user = await getUserByToken(authModel.api_token);
        saveCurrentUser(user);
        navigate('/dashboard'); // Redirect to the dashboard or another appropriate page
      } else if (token) {
        const signUpInfo = {
          token: token,
          provider: SocialLogins.Google
        }
        if (brandSettings.hideSignup !== true) {
          navigate('/auth/social-signup', { state: signUpInfo })
        } else {
          toast.error('User not registered!'.toTranslatedDisplay());
        }
      }
      else {
        console.error('Social login failed');
        setGoogleLoading(false);
      }
    } catch (error) {
      console.error('Error during Google sign-in:', error);
      setGoogleLoading(false);
    }
  };

  // NEW: Handle Apple Sign-In
  const handleAppleSignIn = async (appleData: {
    code: string;
    id_token: string;
    user?: any;
    state?: string;
  }) => {
    setAppleLoading(true);
    try {
      // Send Apple ID token to backend using the same socialLogin function
      const result = await socialLogin({
        sToken: appleData.id_token,
        provider: SocialLogins.Apple,
        onlyLogin: false,
        loginInfo: collectLoginInfo(),
      });

      if (result) {
        // Successful login
        authModel.api_token = result.token;
        saveAuth(authModel);
        const user = await getUserByToken(authModel.api_token);
        saveCurrentUser(user);
        navigate('/dashboard');
      } else if (appleData.id_token) {
        // User needs to sign up
        const signUpInfo = {
          token: appleData.id_token,
          provider: SocialLogins.Apple,
          // Include user data if available (only on first sign-in)
          userData: appleData.user
        }
        if (brandSettings.hideSignup !== true) {
          navigate('/auth/social-signup', { state: signUpInfo })
        } else {
          toast.error('User not registered!'.toTranslatedDisplay());
          setAppleLoading(false);
        }
      } else {
        console.error('Apple social login failed');
        setAppleLoading(false);
      }
    } catch (error) {
      console.error('Error during Apple sign-in:', error);
      toast.error('Apple sign-in failed. Please try again.'.toTranslatedDisplay());
      setAppleLoading(false);
    }
  };

  useEffect(() => {
    const themeModeValue = localStorage.getItem('kt_theme_mode_value');

    // Function to initialize Google Identity Services SDK
    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts) {
        // Initialize Google Identity Services SDK
        window.google.accounts.id.initialize({
          client_id: '925718632458-3e5u51f0ne71jj4qk5ftpo3dk65csf2m.apps.googleusercontent.com', // Replace with your client ID
          callback: handleGoogleSignIn,
        });

        // Render Google Sign-In Button
        const googleBtnContainer = document.getElementById('google-signin-btn');
        const containerWidth = googleBtnContainer?.offsetWidth || 250;
        window.google.accounts.id.renderButton(
          googleBtnContainer,
          {
            theme: themeModeValue === 'dark' ? 'filled_black' : 'outline',
            size: 'large',
            width: Math.min(containerWidth, 400),
          }
        );

        // Optionally, to automatically prompt the sign-in pop-up
        // window.google.accounts.id.prompt();
      } else {
        console.error('Google API not loaded');
      }
    };

    // Load the Google Identity Services SDK if it's not already loaded
    const loadGoogleIdentityScript = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn; // Initialize when the script is loaded
      document.body.appendChild(script);
    };

    // Check if the script is already present
    if (!window.google) {
      loadGoogleIdentityScript();
    } else {
      initializeGoogleSignIn();
    }
  }, []);


  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        const loginResponse = await doLogin({ email: values.email, password: values.password });
        if (loginResponse) {
          authModel.api_token = loginResponse.token;
          saveAuth(authModel);
          saveCurrentUser(loginResponse.user);
        } else {
          throw new Error('Login Failed');
        }
      } catch (error) {
        console.error(error);
        saveAuth(undefined);
        setStatus('The login details are incorrect'.toTranslatedDisplay());
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  return (
    <>
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/* begin::Heading */}
        <div className='mb-5'>
          <h1 className='text-gray-900 fw-bolder mb-3'>{'Sign In'.toTranslatedDisplay()}</h1>
          <div className='text-gray-500 fw-semibold fs-6'>{'Welcome back — your books are up to date.'.toTranslatedDisplay()}</div>
        </div>
        {/* end::Heading */}

        {/* begin::Login options */}
        <div className='row g-3 mb-9 myb-social-row'>
          {/* begin::Col */}
          <div className='col-md-6' style={{ colorScheme: "light" }}>
            {googleLoading ? (
              <button className="btn btn-flex btn-outline btn-text-gray-700 bg-state-light flex-center text-nowrap w-100" disabled>
                <span className='spinner-border spinner-border-sm me-2'></span>
                {'Signing in with Google...'.toTranslatedDisplay()}
              </button>
            ) : (
              <div className='myb-google-btn'>
                {/* Visible face matches the Apple button; Google's own (unstyleable) button sits invisibly on top to receive the click */}
                <div className="btn btn-flex btn-outline btn-text-gray-700 bg-state-light flex-center text-nowrap w-100">
                  <svg width='16' height='16' viewBox='0 0 18 18' aria-hidden='true' className='me-3'><path fill='#4285F4' d='M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.4h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9a8.6 8.6 0 0 0 2.7-6.5z' /><path fill='#34A853' d='M9 18c2.4 0 4.5-.8 6-2.3l-2.9-2.2c-.8.6-1.9.9-3.1.9a5.4 5.4 0 0 1-5.1-3.7H.9v2.3A9 9 0 0 0 9 18z' /><path fill='#FBBC05' d='M3.9 10.7a5.4 5.4 0 0 1 0-3.4V5H.9a9 9 0 0 0 0 8l3-2.3z' /><path fill='#EA4335' d='M9 3.6c1.3 0 2.5.5 3.4 1.3l2.6-2.6A9 9 0 0 0 .9 5l3 2.3A5.4 5.4 0 0 1 9 3.6z' /></svg>
                  {'Sign in with Google'.toTranslatedDisplay()}
                </div>
                <div id="google-signin-btn" className='myb-google-hit'></div>
              </div>
            )}
          </div>
          {/* end::Col */}

          {/* begin::Col */}
          {/* <div className='col-md-9'> */}
          <AppleSignInButton
            toAbsoluteUrl={toAbsoluteUrl}
            onAppleSignIn={handleAppleSignIn}
            className="btn btn-flex btn-outline btn-text-gray-700 btn-active-color-primary bg-state-light flex-center text-nowrap w-100"
            isLoading={appleLoading}
            loadingText={'Signing in with Apple...'.toTranslatedDisplay()}
          />

          {/* </div> */}
          {/* end::Col */}
        </div>
        {/* end::Login options */}

        {/* begin::Separator */}
        <div className='separator separator-content my-5'>
          <span className='w-125px text-gray-500 fw-semibold fs-7'>{'Or with email'.toTranslatedDisplay()}</span>
        </div>
        {/* end::Separator */}

        {formik.status ? (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        ) : brandSettings.showForMainBrand ? (
          <div className='mb-5 bg-light-info p-5 rounded'>
            <div className='text-info'>
              {'Check out all new myBooks,'.toTranslatedDisplay()} <Link to='/demo' className='fw-bolder text-decoration-underline'>{'click here'.toTranslatedDisplay()}</Link> {'to login to demo account'.toTranslatedDisplay()}
            </div>
          </div>
        ) : null}

        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          <label className='form-label fs-6 fw-bolder text-gray-900'>{'Email'.toTranslatedDisplay()}</label>
          <input
            placeholder={'Email'.toTranslatedDisplay()}
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control bg-transparent',
              { 'is-invalid': formik.touched.email && formik.errors.email },
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
            type='email'
            name='email'
            autoComplete='off'
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <span role='alert'>{formik.errors.email?.toTranslatedDisplay()}</span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='fv-row mb-3'>
          <label className='form-label fw-bolder text-gray-900 fs-6 mb-0'>{'Password'.toTranslatedDisplay()}</label>
          <div className='input-group'>
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
            <span
              className='input-group-text'
              onClick={togglePasswordVisibility}
              style={{ cursor: 'pointer' }}
            >
              {showPassword ? (
                <i className='bi bi-eye-slash'></i>
              ) : (
                <i className='bi bi-eye'></i>
              )}
            </span>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password?.toTranslatedDisplay()}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Wrapper */}
        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-5'>
          <div />

          {/* begin::Link */}
          <Link to='/auth/forgot-password' className='link-primary'>
            {'Forgot Password ?'.toTranslatedDisplay()}
          </Link>
          {/* end::Link */}
        </div>
        {/* end::Wrapper */}

        {/* begin::Action */}
        <div className='d-grid mb-10'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-primary'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>{'Continue'.toTranslatedDisplay()}</span>}
            {loading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                {'Please wait...'.toTranslatedDisplay()}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Action */}
        {!brandSettings.hideSignup &&
          <div className='text-gray-500 text-center fw-semibold fs-6'>
            {'Not a Member yet?'.toTranslatedDisplay()}{' '}
            <Link to='/auth/signup' className='link-primary'>
              {'Sign up'.toTranslatedDisplay()}
            </Link>
          </div>
        }
      </form>
    </>
  );
}

